import React from 'react';
import PropTypes from 'prop-types';
import { path } from 'ramda';

import { mainHeader, subHeader, subjectArea } from './Info.less';

import {
  mapFSMCTotalToFatigueLevel,
  mapFSMCPhysicalToFatigueLevel,
  mapFSMCMentalToFatigueLevel
} from 'neuro-calculation-commons';

const fatigueLevelLocalizationsSV = {
  none: 'På basis av förfrågan upplever du inte av utmattning',
  mild: 'På basis av förfrågan upplever du en lindrig känsla av utmattning',
  moderate: 'På basis av förfrågan upplever du en måttlig känsla av utmattning',
  severe: 'På basis av förfrågan upplever du en betydande känsla av utmattning'
};

const physicalFatigueLevelLocalizationsSV = {
  none: 'På basis av förfrågan upplever du inte av fysisk utmattning',
  mild: 'På basis av förfrågan upplever du en lindrig känsla av fysisk utmattning',
  moderate: 'På basis av förfrågan upplever du en måttlig känsla av fysisk utmattning',
  severe: 'På basis av förfrågan upplever du en betydande känsla av fysisk utmattning'
};

const cognitiveFatigueLevelLocalizationsSV = {
  none: 'På basis av förfrågan upplever du inte av kognitiv utmattning',
  mild: 'På basis av förfrågan upplever du en lindrig känsla av kognitiv utmattning',
  moderate: 'På basis av förfrågan upplever du en måttlig känsla av kognitiv utmattning',
  severe: 'På basis av förfrågan upplever du en betydande känsla av kognitiv utmattning'
};

const InfoSV = ({ totalScore, statusMood, document }) => (
  <React.Fragment>
    <div className={mainHeader}>
      Sammandrag av den utmattning som du upplever (på basis av den ifyllda FSMC förfrågan)
    </div>

    <div className={subjectArea}>
      <div className={subHeader}>Utmattningskänslan</div>
      <ul>
        <li>
          Du fick i förfrågan <span style={{ fontWeight: 600 }}>{totalScore}</span> poäng (minsta möjliga poängtal 0,
          största möjliga 100).
        </li>
        {<li>{fatigueLevelLocalizationsSV[mapFSMCTotalToFatigueLevel(totalScore)]}</li>}
      </ul>
    </div>

    <div className={subjectArea}>
      <div className={subHeader}>En känsla av fysisk utmattning</div>
      <ul>
        <li>
          Du fick i den del som kartlägger fysisk utmattning{' '}
          <span style={{ fontWeight: 600 }}>{path(['scores', 'physical'], document)}</span> poäng (minsta möjliga
          poängtal 0, största möjliga 50)
        </li>
        {
          <li>
            {physicalFatigueLevelLocalizationsSV[mapFSMCPhysicalToFatigueLevel(path(['scores', 'physical'], document))]}
          </li>
        }
      </ul>
    </div>

    <div className={subjectArea}>
      <div className={subHeader}>En känsla av kognitiv utmattning (=tankeverksamheterna utmattas)</div>
      <ul>
        <li>
          Du fick i den del som kartlägger den kognitiva utmattningen{' '}
          <span style={{ fontWeight: 600 }}>{path(['scores', 'mental'], document)}</span> poäng (minsta möjliga poängtal
          0, största möjliga 50)
        </li>
        {
          <li>
            {cognitiveFatigueLevelLocalizationsSV[mapFSMCMentalToFatigueLevel(path(['scores', 'mental'], document))]}
          </li>
        }
      </ul>
    </div>

    {statusMood > 0 && (
      <div className={subjectArea}>
        <span>
          Det är bra att lägga märke till att den utmattnings du upplever åtminstone delvis kan förklaras av den
          nedstämda sinnesstämning som du upplever. Du tar väl upp en eventuell nedstämd sinnesstämning vid samtal med
          MS-skötaren eller din läkare.
        </span>
      </div>
    )}

    <div className={subjectArea}>
      <div className={subHeader}>Information om utmattning</div>
      Utmattningen verkar ofta vara ett symtom som är svårt att hantera. Ibland löper uppgifterna och förpliktelserna
      som planerat, ibland bereder en överraskande utmattning svårigheter. Man klarar sig bättre med utmattningen, då
      man känner till när den typiskt framträder, vilka faktorer som gör den starkare och vilka som gör den lättare och
      hur den påverkar handlingsförmågan. Med hjälp av förfrågan fick du information om huruvida din känsla av
      utmattning mera är en fysisk eller en kognitiv utmattning. Ett bra sätt att identifiera faktorer som påverkar
      känslan av utmattning är att föra en dagbok över verksamheten. Bokför exempelvis i en veckas tid följande saker i
      dagboken: hur du sov, när och vad du åt, de dagliga fysiska aktiviteterna, de dagliga arbetsuppgifterna och de
      sociala kontakterna. Bokför till exempel dagtid med, två timmars mellanrum, graden av utmattning som du upplever
      (0 = inte alls utmattad – 10 = synnerligen utmattad).  Genom att studera anteckningarna märker du lätt, om sömnen,
      kosten och dina verksamheter är av betydelse för den utmattning du känner. Du kan finna faktorer, som utmattar och
      även faktorer som upprätthåller känslan av att orka.
      <br />
      <br />
      Genom att fästa uppmärksamhet på de egna handlingssätten och faktorerna omkring sig kan man göra det lättare att
      klara sig med utmattningen. Följande tips kan vara dig till hjälp:
    </div>

    <div className={subjectArea}>
      <div className={subHeader}>Hjälp i vardagen</div>
      Sömn
      <br />
      <ul>
        <li>fäst uppmärksamhet på sömnhygienen</li>
        <ul>
          <li>försök nå lugn en stund, innan du går och lägger dig</li>
          <li>om olika slags saker som ska åtgärdas upptar dina tankar, gör en lista över dem före sovdags</li>
          <li>gallra bort alla ovidkommande faktorer som stör</li>
          <li>om exempelvis smärtor eller kontinensproblem stör nattsömnen, ta upp dem vid samtal med din läkare</li>
        </ul>
      </ul>
      <br />
      Kost och vätskebalans
      <ul>
        <li>fäst uppmärksamhet på kostrekommendationerna</li>
        <ul>
          <li>
            i bakgrunden till känslan av trötthet kan det finnas ett energiunderskott – då utmattning hör samman med din
            sjukdom, accentueras betydelsen av rätt kost
          </li>
          <li>sträva efter att äta frukost, lunch, mellanmål, middag och kvällsmål</li>
          <li>var uppmärksam på att inte intervallerna mellanmåltiderna blir alltför långa</li>
          <li>minskade portionsstorlekar och ökade mängder grönsaker kan ge stöd för att orka</li>
          <li>kom ihåg att dricka tillräckligt under hela dagen</li>
        </ul>
      </ul>
      <br />
      Fysisk och kognitiv aktivitet och spara energi
      <ul>
        <li>se till din fysiska funktionsförmåga</li>
        <ul>
          <li>utöva sådan motion som lämpar sig bäst för dig och inte heller tröttar ut dig för mycket</li>
          <li>lämpligt med motion förbättrar välbefinnandet och minskar ofta en känsla av utmattning </li>
        </ul>
        <li>sköt om din kognitiva funktionsförmåga </li>
        <ul>
          <li>
            då man gör meningsfulla saker, förstärks nervsystemets verksamhet, skyddar det mot kognitiva problem och
            stöder välbefinnandet
          </li>
        </ul>
        <li>fäst uppmärksamhet på när, hur och vad du gör </li>
        <ul>
          <li>underhåll en lista över vad som ska göras </li>
          <li>gör de viktigaste sakerna, när du är som piggast </li>
          <li>undvik brådska och situationer, där du har samtidigt många saker på hälft </li>
          <li>reservera tillräckligt med tid för dig själv </li>
          <li>använd handlingssätt, som sparar energi </li>
          <ul>
            <li>använd hjälpmedel </li>
            <li>försök gallra bort störande faktorer </li>
            <li>ge akt på hur du orkar, pausa och vila vid behov </li>
          </ul>
        </ul>
      </ul>
      <br />
      Miljöfaktorerna
      <ul>
        <li>
          om din verksamhet är utsatt för störningar, sträva efter att gallra bort de störande faktorerna, såsom ljud-
          och ljuskällorna
        </li>
        <li>om värme förvärrar dina symtom och orsakar en känsla av utmattning</li>
        <ul>
          <li>undvik bastubad</li>
          <li>sänk rumstemperaturen</li>
          <li>använd sätt att nå svalka, såsom kylförpackningar, avkylda kläder, en kall dusch, vädring osv.</li>
        </ul>
      </ul>
    </div>
    <div className={subjectArea}>
      <div className={subHeader}>Hur gå vidare härifrån</div>
      Det är viktigt att du håller dig aktiv trots en utmattning. Om utmattningen medför utmaningar med att klara sig i
      vardagen, till exempel med att gå vidare i studierna eller klara av arbetet, ta upp saken vid samtal med
      MS-skötaren och/eller din läkare. Tillsammans kan ni bedöma behovet av fortsatta åtgärder. Det är möjligt att
      klara sig med utmattningen med hjälp av handledning och rehabilitering. Du hittar mera information om möjligheter
      till rehabilitering till exempel på Fpa:s webbplats{' '}
      <a href="https://www.kela.fi" target="blank">
        www.kela.fi
      </a>{' '}
      (MS-rehabiliteringskurserna, krävande individuell multiprofessionell rehabilitering enligt prövning på den
      neurologiska linjen, neuropsykologisk rehabilitering, ergoterapi, fysioterapi) och på Neuroförbundets webbplats{' '}
      <a href="https://www.kuntoutukseen.fi" target="blank">
        www.kuntoutukseen.fi
      </a>
      . Det är bra att berätta om symtomen även för dina närstående. Du kan som hjälp utnyttja det sammandrag som du
      fått av denna förfrågan samt handboken Uupumus MS-taudissa -opas som finns på Neuroförbundets webbplats (
      <a href="https://neuroliitto.fi/tuote/uupumus-ms-taudissa/" target="blank">
        Uupumus MS-taudissa
      </a>
      ). I handboken hittar du också en mall för en aktivitetsdagbok.
    </div>
  </React.Fragment>
);

InfoSV.propTypes = {
  totalScore: PropTypes.number,
  statusMood: PropTypes.number,
  document: PropTypes.object
};

export default InfoSV;
